/**
 * Utility functions for filtering and managing bookings
 */

/**
 * Normalize time slot string for comparison
 * Handles variations like "8:00 PM", "8:00PM", "8:00 pm", etc.
 * @param {string} timeSlot - Time slot string
 * @returns {string} Normalized time slot string
 */
export const normalizeTimeSlot = (timeSlot) => {
  if (!timeSlot || typeof timeSlot !== 'string') return '';
  // Trim, normalize spacing, and convert to uppercase for comparison
  return timeSlot.trim().replace(/\s+/g, ' ').toUpperCase();
};

/**
 * Sum booked seats across aggregated slot rows from get-booked-time-slots.
 * @param {Array} bookedSlots - Array of { totalBookedSeats, ... }
 * @returns {number}
 */
export const getTotalBookedSeats = bookedSlots => {
  if (!Array.isArray(bookedSlots) || bookedSlots.length === 0) return 0;
  return bookedSlots.reduce((sum, slot) => sum + (slot?.totalBookedSeats || 0), 0);
};

export const normalizeSessionTypeId = id => {
  if (id == null) return '';
  if (typeof id === 'string') return id;
  if (typeof id === 'object' && typeof id.toString === 'function') return id.toString();
  return String(id);
};

/**
 * Remaining spots for a session card. Always per-slot-minimum (worst case
 * across the occurrences in `bookedSlots`): a "total" pricing camp booking
 * spans the whole camp, so every booking shares one identical start/end and
 * collapses to a single slot here anyway; a "daily" pricing camp lets
 * customers book individual days (server capacity in
 * BookingDataController.js's assertCampCapacityAllowsBooking is checked
 * per exact occurrence overlap, not summed across the camp's whole run), so
 * summing seats across every day here previously showed cards as
 * prematurely "full" once bookings piled up on other, unrelated days.
 */
export const getSessionSpotsLeft = (bookedSlots, maxSpots) => {
  const cap = Math.max(1, Number(maxSpots) || 1);
  if (!Array.isArray(bookedSlots) || bookedSlots.length === 0) return cap;

  return Math.min(
    ...bookedSlots.map(slot => Math.max(0, cap - (slot?.totalBookedSeats || 0)))
  );
};

/**
 * Check if a time slot has available seats
 * @param {string} timeSlot - Time slot string to check (e.g., "8:00 PM")
 * @param {Array} bookedSlots - Array of booked slot objects with { timeSlot, totalBookedSeats }
 * @param {number} maxParticipants - Maximum participants for the session type
 * @returns {Object} { isAvailable: boolean, availableSeats: number, bookedSeats: number }
 */
export const getTimeSlotAvailability = (timeSlot, bookedSlots, maxParticipants) => {
  if (!timeSlot || !maxParticipants || maxParticipants <= 0) {
    return { isAvailable: false, availableSeats: 0, bookedSeats: 0 };
  }

  const normalizedTimeSlot = normalizeTimeSlot(timeSlot);
  
  // Find matching booked slot
  const bookedSlot = bookedSlots.find(
    slot => normalizeTimeSlot(slot.timeSlot) === normalizedTimeSlot
  );

  const bookedSeats = bookedSlot?.totalBookedSeats || 0;
  const availableSeats = Math.max(0, maxParticipants - bookedSeats);
  const isAvailable = availableSeats > 0;

  return {
    isAvailable,
    availableSeats,
    bookedSeats,
  };
};

/**
 * Filter time slots to show only available ones based on bookings
 * @param {Array} timeSlots - Array of time slot strings
 * @param {Array} bookedSlots - Array of booked slot objects
 * @param {number} maxParticipants - Maximum participants for the session type
 * @returns {Array} Array of available time slot strings with availability info
 */
export const filterAvailableTimeSlots = (timeSlots, bookedSlots, maxParticipants) => {
  if (!Array.isArray(timeSlots) || !maxParticipants || maxParticipants <= 0) {
    return [];
  }

  return timeSlots
    .map(timeSlot => {
      const availability = getTimeSlotAvailability(timeSlot, bookedSlots, maxParticipants);
      return {
        timeSlot,
        ...availability,
      };
    })
    .filter(slot => slot.isAvailable)
    .map(slot => slot.timeSlot);
};

/**
 * Get availability info for all time slots (including unavailable ones)
 * Useful for displaying availability status
 * @param {Array} timeSlots - Array of time slot strings
 * @param {Array} bookedSlots - Array of booked slot objects
 * @param {number} maxParticipants - Maximum participants for the session type
 * @returns {Array} Array of objects with timeSlot and availability info
 */
export const getTimeSlotsAvailability = (timeSlots, bookedSlots, maxParticipants) => {
  if (!Array.isArray(timeSlots) || !maxParticipants || maxParticipants <= 0) {
    return [];
  }

  return timeSlots.map(timeSlot => {
    const availability = getTimeSlotAvailability(timeSlot, bookedSlots, maxParticipants);
    return {
      timeSlot,
      ...availability,
    };
  });
};

